import { getStorage, connectStorageEmulator, ref, uploadBytes, getDownloadURL, deleteObject, getBlob } from 'firebase/storage';
import { initFirebase } from '@/lib/firebase';
import { getAuth } from 'firebase/auth';
import type { Document } from '@/types';

export interface Avatar {
  id: string;
  name: string;
  path: string;
  storagePath?: string;
  personality: string;
  traits: string[];
  uploadedBy?: string;
  uploadedAt?: string;
}

export class StorageService {
  private static storage: ReturnType<typeof getStorage> | null = null;
  private static initialized = false;
  
  private static getStorageInstance() {
    if (!this.storage && typeof window !== 'undefined') {
      const app = initFirebase();
      if (app) {
        this.storage = getStorage(app);
        
        if (!this.initialized && process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATORS === 'true') {
          connectStorageEmulator(this.storage, "127.0.0.1", 9199);
          this.initialized = true;
        }
      }
    }

    if (!this.storage) {
      throw new Error('Storage not initialized');
    }

    return this.storage;
  }

  static async copyDocument(sourcePath: string, destinationPath: string): Promise<void> {
    const storage = this.getStorageInstance();
    
    try {
      const sourceRef = ref(storage, sourcePath);
      const destinationRef = ref(storage, destinationPath);

      // Get the metadata and content of the source file
      const sourceData = await getBlob(sourceRef);
      
      // Upload to the new location with the same metadata
      await uploadBytes(destinationRef, sourceData, {
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET',
          'Cache-Control': 'public, max-age=3600'
        }
      });

      console.log('Successfully copied file from', sourcePath, 'to', destinationPath);
    } catch (error) {
      console.error('Error copying document:', error);
      throw error instanceof Error 
        ? new Error(`Copy failed: ${error.message}`)
        : new Error('Copy failed: Unknown error occurred');
    }
  }

  static async uploadDocument(file: File, userId: string, shareId: string): Promise<Document> {
    const storage = this.getStorageInstance();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('User must be authenticated to upload documents');
    }

    if (currentUser.uid !== userId) {
      throw new Error('User ID mismatch. You can only upload to your own directory.');
    }

    try {
      const fileExtension = file.name.split('.').pop() || '';
      const fileName = `${crypto.randomUUID()}.${fileExtension}`;
      const storagePath = `users/${userId}/shares/${shareId}/documents/${fileName}`;
      
      const storageRef = ref(storage, storagePath);
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'Access-Control-Allow-Origin': '*',
          'Access-Control-Allow-Methods': 'GET',
          'Cache-Control': 'public, max-age=3600',
          // Preserve the user's original filename so backend tools (e.g.
          // AILANG Parse) can forward it to upstream services — the GCS
          // object path uses a UUID, which is opaque in dashboards.
          'originalName': file.name
        }
      };

      await uploadBytes(storageRef, file, metadata);
      const downloadUrl = await getDownloadURL(storageRef);

      const fileType = file.type.split('/')[1] || fileExtension;
      
      return {
        name: file.name,
        type: fileType,
        url: downloadUrl,
        storagePath,
        contentType: file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error instanceof Error 
        ? new Error(`Upload failed: ${error.message}`)
        : new Error('Upload failed: Unknown error occurred');
    }
  }

  static async deleteDocument(storagePath: string): Promise<void> {
    const storage = this.getStorageInstance();
    if (!storage) throw new Error('Storage not initialized');
    
    try {
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);
      console.log('Successfully deleted file:', storagePath);
    } catch (error) {
      // Check if the error is because the file doesn't exist
      if (error instanceof Error && error.message.includes('object-not-found')) {
        console.warn('File already deleted or not found:', storagePath);
        return; // Don't throw error for non-existent files
      }
      throw error; // Re-throw other types of errors
    }
  }

  static async uploadAvatar(file: File): Promise<Avatar> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('User must be authenticated to upload avatars');
    }

    // Validate file
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`Invalid file type. Allowed types: ${ALLOWED_TYPES.join(', ')}`);
    }

    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File too large. Maximum size: ${MAX_FILE_SIZE / 1024 / 1024}MB`);
    }

    // Get the ID token
    const idToken = await currentUser.getIdToken();

    // Create form data
    const formData = new FormData();
    formData.append('file', file);

    // Upload via API
    const response = await fetch('/api/avatars/upload', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload avatar');
    }

    const result = await response.json();
    return result.avatar;
  }

  static validateAvatarImage(file: File): { valid: boolean; error?: string } {
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const MIN_DIMENSION = 200;
    const MAX_DIMENSION = 2000;

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return { 
        valid: false, 
        error: `Invalid file type. Please upload a JPEG, PNG, or WebP image.` 
      };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { 
        valid: false, 
        error: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.` 
      };
    }

    // Further dimension validation would require reading the image
    // For now, we'll do that on the client side
    return { valid: true };
  }
}

export default StorageService;